/**
 * Vehicle modal — handles BOTH first-time registration and replacement of an
 * existing vehicle. Two-step flow:
 *   1. Enter plate.
 *   2. Confirm ownership month/year — verified against the gov registry.
 *
 * Mode is auto-detected at `open()` time from `LocalState.plate`:
 *   - empty → REGISTER mode (user is logged in via SMS-only but has no car yet)
 *   - set   → REPLACE  mode (legacy 3-step flow OR user swapping plates)
 *
 * All copy (header, prompts, buttons, busy state, success alert) and the
 * "same-as-current" guard switch on mode. The underlying service call is
 * `vehicle.service.replace()` in both cases — it handles old-plate detach
 * gracefully when there's no old plate.
 */

import { ref } from '../../core/firebase.js';
import { LocalState } from '../../core/localState.js';
import { Auth } from '../../app/config.js';
import { normalize as normalizePlate, isValid as isValidPlate } from '../../shared/plate.js';
import { normalize as normalizePhone } from '../../shared/phone.js';
import { escape } from '../../shared/escape.js';
import { createLogger } from '../../core/logger.js';
import * as spinner from '../../core/spinner.js';
import * as alert from '../../core/alert.js';

import { replace as replaceVehicle, register as registerVehicle } from './vehicle.service.js';
import { renderSection } from './myCar.view.js';
import { showBlockedAccountModal } from '../../shared/blockedAccountModal.js';

const log = createLogger('replaceVehicle');

const OVERLAY_ID = 'replaceOverlay';
const MIN_YEAR = 1980;

/**
 * Mode flag set at `open()` time and read by every render/step function.
 * `'register'` when LocalState.plate is empty, `'replace'` otherwise.
 * @type {'register' | 'replace'}
 */
let mode = 'replace';

/** Hebrew copy keyed by mode. Centralised so every step+state stays consistent.
 *  step2Sub contains HTML (`<strong>`/`<em>`) — rendered raw, not escaped.
 *  Production year (`shnat_yitzur`) is the verification factor — replaces the
 *  legacy month+year of purchase, which depended on a CKAN dataset that
 *  only covers post-2017 ownership changes. */
const STEP2_SUB =
  'בחר את <strong>שנת הייצור</strong> של הרכב — כפי שמופיעה בתעודת ' +
  'הרישוי. נשווה לרישומי משרד התחבורה לאימות.';

const COPY = /** @type {const} */ ({
  register: {
    headerTitle: 'רישום הרכב שלך',
    step1Prompt: 'הזן את מספר הרכב שלך:',
    step1Next:   'המשך לאימות פרטים',
    step2Prompt: (plate) => `מה שנת הייצור של הרכב <strong dir="ltr">${plate}</strong>?`,
    step2Sub:    STEP2_SUB,
    confirmBtn:  'רשום רכב',
    busyText:    'רושם רכב...',
    successTitle:'הרכב נרשם לחשבונך',
    successMsg:  (plate) => `הרכב ${plate} שויך לחשבונך בהצלחה.`,
  },
  replace: {
    headerTitle: 'החלפת רכב משויך',
    step1Prompt: 'הזן את מספר הרכב החדש לשיוך:',
    step1Next:   'המשך לאימות פרטים',
    step2Prompt: (plate) => `אימות פרטי הרכב <strong dir="ltr">${plate}</strong>`,
    step2Sub:    STEP2_SUB,
    confirmBtn:  'אמת והחלף',
    busyText:    'מחליף רכב...',
    successTitle:'הרכב הוחלף',
    successMsg:  (plate) => `הרכב ${plate} שויך לחשבונך בהצלחה.`,
  },
});

/** Open the modal at step 1. Mode is decided here from LocalState. */
export function open() {
  const currentPlate = normalizePlate(LocalState.plate ?? '');
  mode = currentPlate ? 'replace' : 'register';
  const overlay = ensureModal();
  // Same trick as vehicleDetails.view — re-append on every open so the
  // overlay is the last child of <body> and wins DOM-order stacking
  // disputes against any modal that was created after it.
  document.body.appendChild(overlay);
  // Update the header title (modal was potentially built in the OTHER mode
  // on a previous open; we keep a single DOM node and just mutate text).
  const titleEl = overlay.querySelector('#replaceTitle');
  if (titleEl) titleEl.textContent = COPY[mode].headerTitle;
  renderStep1();
  overlay.classList.add('open');
}

/** Close + reset. */
export function close() {
  const overlay = document.getElementById(OVERLAY_ID);
  if (!overlay) return;
  overlay.classList.remove('open');
  const body = overlay.querySelector('#replaceBody');
  if (body) /** @type {HTMLElement} */ (body).innerHTML = '';
}

// ====================================================================
// internals
// ====================================================================

function ensureModal() {
  let ov = document.getElementById(OVERLAY_ID);
  if (ov) return ov;
  ov = document.createElement('div');
  ov.id = OVERLAY_ID;
  ov.className = 'replace-overlay';
  ov.innerHTML = `
    <aside class="replace-modal" role="dialog" aria-modal="true" aria-labelledby="replaceTitle">
      <header class="replace-header">
        <div id="replaceTitle" class="replace-title">החלפת רכב משויך</div>
        <button class="replace-close" id="replaceCloseBtn" aria-label="סגור">✕</button>
      </header>
      <div id="replaceBody"></div>
    </aside>
  `;
  document.body.appendChild(ov);
  // Intentionally NOT closing on backdrop click. The replace flow is multi-
  // step (plate → ownership confirm) and a stray tap outside the modal would
  // otherwise wipe the user's input. Closing is still available via the ✕
  // button, the "ביטול"/"חזרה" buttons, and the Escape key.
  ov.querySelector('#replaceCloseBtn')?.addEventListener('click', close);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && ov && ov.classList.contains('open')) close();
  });
  return ov;
}

function renderStep1() {
  const body = document.querySelector(`#${OVERLAY_ID} #replaceBody`);
  if (!body) return;
  body.innerHTML = `
    <div class="replace-step">
      <p>${escape(COPY[mode].step1Prompt)}</p>
      <input type="text" id="replacePlateInput" class="replace-input" inputmode="numeric"
             maxlength="8" placeholder="לדוגמה: 1234567" />
      <div id="replaceError" class="replace-error" style="display:none;background:#fef2f2;border:1px solid #fecaca;color:#991b1b;padding:10px 12px;border-radius:8px;margin-top:10px;font-size:13.5px;line-height:1.5;white-space:pre-line;"></div>
      <div class="replace-footer">
        <button id="replaceNextBtn" class="green-button">${escape(COPY[mode].step1Next)}</button>
        <button id="replaceCancelBtn" class="btn btn-secondary">ביטול</button>
      </div>
    </div>
  `;
  body.querySelector('#replaceNextBtn')?.addEventListener('click', onStep1Next);
  body.querySelector('#replaceCancelBtn')?.addEventListener('click', close);
  const input = /** @type {HTMLInputElement | null} */ (body.querySelector('#replacePlateInput'));
  input?.focus();
  input?.addEventListener('keydown', (e) => { if (e.key === 'Enter') onStep1Next(); });
}

function onStep1Next() {
  const input = /** @type {HTMLInputElement | null} */ (document.getElementById('replacePlateInput'));
  const plate = normalizePlate(input?.value ?? '');
  if (!isValidPlate(plate)) {
    showStepError('מספר רכב לא תקין. נדרשות 7 או 8 ספרות.');
    return;
  }
  // Same-as-current guard — only meaningful in REPLACE mode.
  if (mode === 'replace' && plate === normalizePlate(LocalState.plate ?? '')) {
    showStepError('זהו הרכב הנוכחי שלך. בחר רכב שונה.');
    return;
  }
  renderStep2(plate);
}

/** @param {string} plate */
function renderStep2(plate) {
  const body = document.querySelector(`#${OVERLAY_ID} #replaceBody`);
  if (!body) return;
  const yearOpts = buildYearOptions();
  body.innerHTML = `
    <div class="replace-step">
      <p>${COPY[mode].step2Prompt(escape(plate))}</p>
      <p style="color:#6b7a89;font-size:14px;line-height:1.55;">${COPY[mode].step2Sub}</p>
      <select id="replaceYear" class="replace-input" style="width:100%;">
        ${yearOpts}
      </select>
      <div id="replaceError" class="replace-error" style="display:none;background:#fef2f2;border:1px solid #fecaca;color:#991b1b;padding:10px 12px;border-radius:8px;margin-top:10px;font-size:13.5px;line-height:1.5;white-space:pre-line;"></div>
      <div class="replace-footer">
        <button id="replaceConfirmBtn" class="green-button">${escape(COPY[mode].confirmBtn)}</button>
        <button id="replaceBackBtn" class="btn btn-secondary">חזרה</button>
      </div>
    </div>
  `;
  body.querySelector('#replaceConfirmBtn')?.addEventListener('click', () => onStep2Confirm(plate));
  body.querySelector('#replaceBackBtn')?.addEventListener('click', renderStep1);
}

/** @param {string} plate */
async function onStep2Confirm(plate) {
  const yearEl = /** @type {HTMLSelectElement | null} */ (document.getElementById('replaceYear'));
  if (!yearEl?.value) {
    showStepError('בחר שנת ייצור.');
    return;
  }
  const year = yearEl.value;

  // Inline button state — the global spinner overlay sits behind the modal
  // and is barely visible; without an on-button indicator the user thinks
  // the app froze and starts double-clicking, which can trigger the rules
  // race we already had in vehicle.service. Disable the button + cancel
  // button while the request is in flight and show a spinner inline.
  const restoreButtons = setReplaceButtonsBusy(true);
  const release = spinner.show('replaceVehicle.confirm');
  let result;
  try {
    // In REGISTER mode we route to `register()` (no oldPlate to release); in
    // REPLACE we use `replace()`. Both verify via production year now.
    result = mode === 'register'
      ? await registerVehicle({ plate, year })
      : await replaceVehicle({ newPlate: plate, year });
  } finally {
    release();
    restoreButtons();
  }

  if (result.status === 'ok') {
    // Lift the legacy search-block IMMEDIATELY. `searchVehicle()` in
    // www/modals/search-vehicle.js gates on `window.isOwnershipMismatched`;
    // the previous plate's mismatch is no longer relevant since
    // vehicle.service.js just gov-verified the new plate's ownership.
    /** @type {any} */ (window).isOwnershipMismatched = false;

    await alert.showAlert({
      title: COPY[mode].successTitle,
      message: COPY[mode].successMsg(plate),
    });
    close();
    try { await renderSection(); } catch (e) { log.warn('refresh after replace:', e); }
    return;
  }
  if (result.status === 'mismatch') {
    // "Plate not in gov registry" — show the message but DON'T penalise the
    // attempts counter (might be a legit edge case for newly-registered plates).
    if (!(/** @type {any} */ (result).govYear)) {
      showStepError(result.message ?? 'לא נמצא רישום של רכב זה במאגר משרד התחבורה.');
      return;
    }
    // Real year mismatch — record the attempt FIRST so we can include the
    // updated "X attempts remaining" info in the same message we show.
    const info = await recordFailedAttempt();
    if (info.blocked) {
      // Final strike — close the wizard, force-logout IMMEDIATELY (in
      // parallel so the user can't keep using the app), then show a
      // friendly popup with a "Contact support" button that opens the
      // mail client pre-filled.
      close();
      try { /** @type {any} */ (window).hardClientLogout?.('blocked_attempts'); }
      catch (_) { /* ignore — popup still fires */ }
      await showBlockedAccountModal({ context: 'replace' });
      return;
    }
    // Build a multi-line error with the mismatch + remaining attempts +
    // what'll happen next. Severity ramps up on the last attempt.
    const baseMsg = result.message ?? 'שנת הייצור שהוזנה אינה תואמת לרישומי משרד התחבורה.';
    if (info.remaining === 1) {
      // Last attempt — make this jump out visually
      showStepError(
        `⚠️ אזהרה אחרונה: ${baseMsg}\n` +
        `נשאר לך נסיון אחד בלבד. עוד נסיון שגוי יחסום את החשבון לצמיתות לבקשות שיוך רכב.`,
      );
    } else {
      showStepError(
        `${baseMsg}\n` +
        `נותרו לך ${info.remaining} נסיונות נוספים מתוך ${Auth.maxAttemptsPerDay}. ` +
        `אחרי ${Auth.maxAttemptsPerDay} נסיונות שגויים החשבון יחסם לצמיתות.`,
      );
    }
    return;
  }
  if (result.status === 'taken') {
    showStepError(result.message ?? 'הרכב משויך כבר למשתמש אחר.');
    return;
  }
  showStepError(result.message ?? 'שגיאה לא ידועה.');
}

/**
 * Switch the "אמת והחלף" + "חזרה" buttons into a busy state — disabled,
 * confirm button shows an inline spinner with "מחליף רכב...". Returns a
 * restore function that flips them back regardless of outcome.
 *
 * @param {boolean} busy
 */
function setReplaceButtonsBusy(busy) {
  const confirm = /** @type {HTMLButtonElement | null} */ (document.getElementById('replaceConfirmBtn'));
  const back    = /** @type {HTMLButtonElement | null} */ (document.getElementById('replaceBackBtn'));
  if (!confirm || !busy) return () => {};

  ensureSpinnerStyles();
  const originalConfirmHTML = confirm.innerHTML;
  const originalConfirmDisabled = confirm.disabled;
  const originalBackDisabled = back ? back.disabled : false;

  confirm.disabled = true;
  confirm.setAttribute('aria-busy', 'true');
  confirm.style.opacity = '0.85';
  confirm.style.cursor = 'wait';
  confirm.innerHTML =
    '<span class="replace-spinner" aria-hidden="true"></span>' +
    `<span>${escape(COPY[mode].busyText)}</span>`;
  if (back) back.disabled = true;

  return () => {
    confirm.innerHTML = originalConfirmHTML;
    confirm.disabled = originalConfirmDisabled;
    confirm.removeAttribute('aria-busy');
    confirm.style.opacity = '';
    confirm.style.cursor = '';
    if (back) back.disabled = originalBackDisabled;
  };
}

let __replaceSpinnerStyles = false;
function ensureSpinnerStyles() {
  if (__replaceSpinnerStyles) return;
  __replaceSpinnerStyles = true;
  const style = document.createElement('style');
  style.textContent = `
    .replace-spinner {
      display: inline-block;
      width: 16px; height: 16px;
      border: 2.5px solid rgba(255, 255, 255, 0.35);
      border-top-color: #ffffff;
      border-radius: 50%;
      animation: replace-spinner-rot 0.8s linear infinite;
      vertical-align: middle;
      margin-inline-end: 8px;
    }
    @keyframes replace-spinner-rot { to { transform: rotate(360deg); } }
  `;
  document.head.appendChild(style);
}

/** @param {string} message */
function showStepError(message) {
  const err = document.getElementById('replaceError');
  if (err) {
    err.textContent = message;
    err.style.display = 'block';
  }
}

function buildYearOptions() {
  const opts = ['<option value="" disabled selected>שנת ייצור</option>'];
  const current = new Date().getFullYear();
  for (let y = current; y >= MIN_YEAR; y--) opts.push(`<option value="${y}">${y}</option>`);
  return opts.join('');
}

/**
 * Mirror the legacy failed-attempt tracking on `attempts/{phone}`. Three
 * failures per day lock the user out of the replace flow.
 *
 * @returns {Promise<{ count: number; remaining: number; blocked: boolean }>}
 *          Counters AFTER this attempt was recorded so the caller can build
 *          a clear "you have N attempts left" message for the user.
 */
async function recordFailedAttempt() {
  const phone = normalizePhone(LocalState.phone ?? '');
  if (!phone) return { count: 0, remaining: Auth.maxAttemptsPerDay, blocked: false };
  const today = new Date().toISOString().split('T')[0];
  try {
    const r = ref(`attempts/${phone}`);
    const snap = await r.once('value');
    const data = snap.val() ?? {};
    let count = data.date === today ? (data.count || 0) + 1 : 1;
    const blocked = count >= Auth.maxAttemptsPerDay;
    const status = blocked ? 'blocked_permanently' : '';
    await r.set({ date: today, count, status });
    return {
      count,
      remaining: Math.max(0, Auth.maxAttemptsPerDay - count),
      blocked,
    };
  } catch (e) {
    log.warn('recordFailedAttempt:', e);
    return { count: 0, remaining: Auth.maxAttemptsPerDay, blocked: false };
  }
}
